// frontend/src/site/editor/SiteEditor.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../SiteContext';

import ThemePicker, { presetToStylePalette } from './ThemePicker';
import PreviewFrame from './PreviewFrame';

import SiteContentEditor from '../builder/SiteContentEditor';
import { getModelSections } from '../builder/siteSections';
import { migrateConteudo } from './migrateConteudo';

function asObj(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

function pickThemeDraft(cfg) {
  const c = asObj(cfg);
  return {
    modelId: String(c.modelId || 'corretor'),
    styleId: String(c.styleId || 'humano'),
    paletteId: String(c.paletteId || 'a'),
    preset: String(c.preset || 'humano_a'),

    // ✅ novos ajustes (preview do card)
    layoutDensity: String(c.layoutDensity || 'normal'),
    buttonStyle: String(c.buttonStyle || 'solid'),
    cardStyle: String(c.cardStyle || 'soft'),
  };
}

function sameThemeDraft(a, b) {
  const A = pickThemeDraft(a);
  const B = pickThemeDraft(b);
  return (
    A.modelId === B.modelId &&
    A.styleId === B.styleId &&
    A.paletteId === B.paletteId &&
    A.preset === B.preset &&
    A.layoutDensity === B.layoutDensity &&
    A.buttonStyle === B.buttonStyle &&
    A.cardStyle === B.cardStyle
  );
}

// Defaults mínimos só pro bridge (shape)
const DEFAULTS_BY_MODEL_EDITOR = {
  corretor: {
    conteudo: {
      hero: {},
      sobre: {},
      imoveis: {},
      servicos: {},
      provaSocial: {},
      ctaFinal: {},
      rodape: {},
    },
  },
  imobiliaria: {
    conteudo: {
      hero: {},
      destaques: {},
      terrenos: {},
      ctaFinal: {},
      rodape: {},
    },
  },
};

export default function SiteEditor({
  // ✅ NOVO:
  // Quando definido, o SiteEditor respeita esse modelId como fonte única
  // e trava o seletor de modelo no ThemePicker.
  forcedModelId,
}) {
  const { usuario } = useAuth();
  const uid = usuario?.uid;

  // ✅ BLINDAGEM: se o Provider não estiver presente, não quebra o app inteiro.
  const site = useSite();

  if (!site) {
    return (
      <div className="p-6 rounded-2xl border border-gray-200 bg-white">
        <h2 className="text-base font-semibold text-gray-900">
          Editor do Site
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Carregando configurações do site...
        </p>
      </div>
    );
  }

  const {
    perfilSite,
    ownerWhatsUrl,

    // ✅ Builder state
    modelId,
    setModelId, // opcional (se existir no context)
    setEstrutura,
    estrutura,
    conteudo,
    setConteudo,

    // ✅ Theme draft do Context (ETAPA 04)
    siteConfig,
    setSiteConfig,
    saveSiteThemeConfig,

    // ✅ Save builder
    saveBuilder,
    savingBuilder,
    builderError,
    builderSuccess,

    // ✅ Theme final (já reage ao draft no Context)
    theme,
  } = site;

  const lockModel = Boolean(forcedModelId);
  const forced = lockModel ? String(forcedModelId) : null;

  // -------------------------
  // Estado local "themeMeta" (UI do ThemePicker)
  // -------------------------
  const initial = useMemo(() => {
    const cfg = asObj(perfilSite?.siteConfig);

    const preset = String(cfg.preset || 'humano_a');
    const sp = presetToStylePalette(preset);

    // ✅ modelo inicial:
    // 1) forcedModelId (se existir)
    // 2) cfg.modelId salvo
    // 3) modelId do builder
    // 4) fallback
    const initModel = String(forced || cfg.modelId || modelId || 'corretor');

    return {
      modelId: initModel,

      // ✅ se existir salvo, respeita; se não, infere do preset
      styleId: String(cfg.styleId || sp.styleId),
      paletteId: String(cfg.paletteId || sp.paletteId),
      preset,

      // ✅ novos ajustes do preview do card
      layoutDensity: String(cfg.layoutDensity || 'normal'),
      buttonStyle: String(cfg.buttonStyle || 'solid'),
      cardStyle: String(cfg.cardStyle || 'soft'),
    };
  }, [perfilSite?.siteConfig, modelId, forced]);

  const [themeMeta, setThemeMeta] = useState(initial);

  // guard para não disparar migração na carga inicial
  const didInitRef = useRef(false);

  // ✅ sempre que "initial" mudar (troca de uid/perfil/forced), reseta themeMeta
  useEffect(() => {
    setThemeMeta(initial);
    didInitRef.current = false;
  }, [initial]);

  // ✅ Quando forcedModelId mudar (seleção no EditarSiteTab),
  // atualiza imediatamente o modelId do themeMeta (sem mexer em estilo/paleta)
  useEffect(() => {
    if (!forced) return;
    setThemeMeta((prev) => {
      if (String(prev?.modelId) === String(forced)) return prev;
      return { ...prev, modelId: String(forced) };
    });
  }, [forced]);

  // ✅ Sempre que themeMeta muda, aplicamos no draft do Context
  useEffect(() => {
    const nextDraft = {
      ...asObj(siteConfig),
      modelId: themeMeta.modelId,
      styleId: themeMeta.styleId,
      paletteId: themeMeta.paletteId,
      preset: themeMeta.preset,

      // ✅ novos ajustes
      layoutDensity: themeMeta.layoutDensity,
      buttonStyle: themeMeta.buttonStyle,
      cardStyle: themeMeta.cardStyle,
    };

    if (!sameThemeDraft(siteConfig, nextDraft)) {
      setSiteConfig?.(nextDraft);
    }

    // opcional: manter modelId do builder em sync
    if (
      typeof setModelId === 'function' &&
      themeMeta.modelId &&
      themeMeta.modelId !== modelId
    ) {
      setModelId(themeMeta.modelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    themeMeta.modelId,
    themeMeta.styleId,
    themeMeta.paletteId,
    themeMeta.preset,
    themeMeta.layoutDensity,
    themeMeta.buttonStyle,
    themeMeta.cardStyle,
  ]);

  // ✅ Troca de modelo com bridge (conteúdo não some)
  useEffect(() => {
    const nextModel = String(themeMeta.modelId || modelId || 'corretor');
    const prevModel = String(modelId || 'corretor');

    // ✅ primeira carga: não migra (evita mexer sem o usuário pedir)
    if (!didInitRef.current) {
      didInitRef.current = true;
      return;
    }

    // ✅ se já está igual, não faz nada (evita reprocessamento)
    if (nextModel === prevModel) return;

    // estrutura oficial do novo modelo
    setEstrutura(getModelSections(nextModel));

    // migração de conteúdo (bridge)
    const nextConteudo = migrateConteudo({
      fromModelId: prevModel,
      toModelId: nextModel,
      conteudoAtual: conteudo,
      defaultsByModel: DEFAULTS_BY_MODEL_EDITOR,
    });

    setConteudo(nextConteudo);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMeta.modelId]);

  async function handleSaveAll() {
    if (!uid) return;

    // 1) salva builder (estrutura + conteúdo)
    const respBuilder = await saveBuilder();
    if (respBuilder && respBuilder.ok === false) return;

    // 2) salva siteConfig (tema/meta)
    if (typeof saveSiteThemeConfig === 'function') {
      await saveSiteThemeConfig({
        ...asObj(siteConfig),
        modelId: themeMeta.modelId,
        styleId: themeMeta.styleId,
        paletteId: themeMeta.paletteId,
        preset: themeMeta.preset,

        // ✅ novos ajustes
        layoutDensity: themeMeta.layoutDensity,
        buttonStyle: themeMeta.buttonStyle,
        cardStyle: themeMeta.cardStyle,
      });
    }
  }

  // ✅ força re-render do PreviewFrame quando mudar tema/meta (evita “travado”)
  const previewKey = useMemo(
    () =>
      [
        themeMeta.modelId,
        themeMeta.styleId,
        themeMeta.paletteId,
        themeMeta.preset,
        themeMeta.layoutDensity,
        themeMeta.buttonStyle,
        themeMeta.cardStyle,
      ].join('|'),
    [
      themeMeta.modelId,
      themeMeta.styleId,
      themeMeta.paletteId,
      themeMeta.preset,
      themeMeta.layoutDensity,
      themeMeta.buttonStyle,
      themeMeta.cardStyle,
    ]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[560px_1fr] gap-4">
      {/* PAINEL ESQUERDO */}
      <div className="space-y-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Editor do Site
              </h2>
              <p className="text-sm text-gray-500">
                Ajuste tema, seções e conteúdo. Preview em tempo real.
              </p>
            </div>

            <button
              type="button"
              onClick={handleSaveAll}
              disabled={savingBuilder}
              className="px-4 py-2 rounded-lg bg-black text-white hover:opacity-90 disabled:opacity-60 text-sm"
            >
              {savingBuilder ? 'Salvando...' : 'Salvar'}
            </button>
          </div>

          {(builderError || builderSuccess) && (
            <div
              className={`mt-3 p-3 rounded-lg border text-sm ${
                builderError
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-green-200 bg-green-50 text-green-700'
              }`}
            >
              {builderError || builderSuccess}
            </div>
          )}
        </div>

        {/* Tema */}
        <ThemePicker
          modelId={themeMeta.modelId}
          styleId={themeMeta.styleId}
          paletteId={themeMeta.paletteId}
          preset={themeMeta.preset}
          layoutDensity={themeMeta.layoutDensity}
          buttonStyle={themeMeta.buttonStyle}
          cardStyle={themeMeta.cardStyle}
          lockModel={lockModel} // ✅ trava o modelo quando vem do EditarSiteTab
          onChange={(next) =>
            setThemeMeta((prev) => ({
              ...prev,
              ...next,
              // ✅ proteção extra: se lockModel, respeita forcedModelId
              modelId: lockModel
                ? String(forced)
                : String(next?.modelId || prev.modelId),
            }))
          }
        />

        {/* Conteúdo por seção */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <SiteContentEditor
            modelId={themeMeta.modelId}
            estrutura={estrutura}
            conteudo={conteudo}
            onChange={(nextConteudo) => setConteudo(nextConteudo)}
            debug={false}
          />
        </div>
      </div>

      {/* PREVIEW DIREITA */}
      <div className="lg:sticky lg:top-4 self-start">
        <PreviewFrame
          key={previewKey}
          modelId={themeMeta.modelId}
          handle={(perfilSite?.siteHandle || 'preview').toString()}
          theme={{
            ...(theme || { mode: 'light' }),
            // ✅ garante que o PreviewFrame “veja” os novos knobs
            modelId: themeMeta.modelId,
            styleId: themeMeta.styleId,
            paletteId: themeMeta.paletteId,
            preset: themeMeta.preset,
            layoutDensity: themeMeta.layoutDensity,
            buttonStyle: themeMeta.buttonStyle,
            cardStyle: themeMeta.cardStyle,
          }}
          estrutura={estrutura}
          conteudo={conteudo}
          perfilSite={perfilSite}
          ownerWhatsUrl={ownerWhatsUrl}
        />
      </div>
    </div>
  );
}
